import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { preferencesApi, UserPreferences } from '../services/api';
import { useAuth } from '../context/AuthContext';
import PreferencesEditor from '../components/PreferencesEditor';
import Button from '../components/Button';
import PageTransition from '../components/PageTransition';

const EMPTY: Omit<UserPreferences, 'isConfigured'> = {
  likedCuisines: [],
  dislikedCuisines: [],
  likedMenuTags: [],
  dislikedMenuTags: [],
};

const PreferencesSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const { updatePreferencesConfigured } = useAuth();
  const [prefs, setPrefs] = useState<Omit<UserPreferences, 'isConfigured'>>(EMPTY);
  const [saving, setSaving] = useState(false);

  const totalSelected =
    prefs.likedCuisines.length +
    prefs.dislikedCuisines.length +
    prefs.likedMenuTags.length +
    prefs.dislikedMenuTags.length;

  const handleSave = async () => {
    setSaving(true);
    try {
      await preferencesApi.update(prefs);
      updatePreferencesConfigured(true);
      navigate('/');
    } catch {
      /* ignore */
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    try {
      await preferencesApi.update(EMPTY);
    } catch {/* ignore */}
    updatePreferencesConfigured(true);
    navigate('/');
  };

  return (
    <PageTransition className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 py-4 sticky top-0 z-10">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-gray-900">Ваши предпочтения</h1>
            <p className="text-xs text-gray-400 mt-0.5">Поможем подобрать подходящие места</p>
          </div>
          <button
            onClick={handleSkip}
            className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            Пропустить
          </button>
        </div>
      </div>

      {/* Hint */}
      <div className="max-w-md mx-auto px-4 pt-5 pb-2">
        <div className="bg-orange-50 border border-orange-100 rounded-xl px-4 py-3 flex items-start gap-3">
          <svg className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <p className="text-xs text-orange-700">
            Нажмите на тег один раз — <strong>нравится</strong>, ещё раз — <strong>не ем</strong>, ещё раз — сброс
          </p>
        </div>
      </div>

      {/* Editor */}
      <div className="max-w-md mx-auto px-4 py-4">
        <PreferencesEditor value={prefs} onChange={setPrefs} />
      </div>

      {/* Sticky footer */}
      <div className="sticky bottom-0 bg-white/90 backdrop-blur-sm border-t border-gray-100 px-4 py-4">
        <div className="max-w-md mx-auto">
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Сохраняем...' : totalSelected > 0 ? `Сохранить (${totalSelected})` : 'Сохранить'}
          </Button>
        </div>
      </div>
    </PageTransition>
  );
};

export default PreferencesSetupPage;
